import React, { useState, useRef, useLayoutEffect, cloneElement } from 'react';
import { cn } from '../../lib/utils';

const DefaultHomeIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);
const DefaultCompassIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" />
  </svg>
);
const DefaultBellIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const defaultNavItems = [
  { id: 'default-home', icon: <DefaultHomeIcon />, label: 'Home' },
  { id: 'default-explore', icon: <DefaultCompassIcon />, label: 'Explore' },
  { id: 'default-notifications', icon: <DefaultBellIcon />, label: 'Notifications' },
];

/**
 * LimelightNav - Alt çizgi efektli adaptif genişlikte navigasyon çubuğu.
 * @param {Object} props
 * @param {Array} props.items - { id, icon, label?, onClick? }
 * @param {number} props.activeIndex - Kontrollü mod: aktif tab indeksi
 * @param {number} props.defaultActiveIndex - Kontrolsüz modda varsayılan
 * @param {Function} props.onTabChange - Tab değiştiğinde (index) => void
 * @param {boolean} props.showLabels - Etiket metinlerini göster
 * @param {string} props.className
 * @param {string} props.limelightClassName
 * @param {string} props.iconContainerClassName
 * @param {string} props.iconClassName
 */
export function LimelightNav({
  items = defaultNavItems,
  activeIndex: controlledActiveIndex,
  defaultActiveIndex = 0,
  onTabChange,
  showLabels = false,
  className,
  limelightClassName,
  iconContainerClassName,
  iconClassName,
}) {
  const [internalActiveIndex, setInternalActiveIndex] = useState(defaultActiveIndex);
  const [isReady, setIsReady] = useState(false);
  const navItemRefs = useRef([]);
  const limelightRef = useRef(null);

  const isControlled = controlledActiveIndex !== undefined;
  const activeIndex = isControlled ? controlledActiveIndex : internalActiveIndex;

  useLayoutEffect(() => {
    if (items.length === 0) return;

    const limelight = limelightRef.current;
    const activeItem = navItemRefs.current[activeIndex];

    if (limelight && activeItem) {
      const newLeft = activeItem.offsetLeft + activeItem.offsetWidth / 2 - limelight.offsetWidth / 2;
      limelight.style.left = `${newLeft}px`;

      if (!isReady) {
        const t = setTimeout(() => setIsReady(true), 50);
        return () => clearTimeout(t);
      }
    }
  }, [activeIndex, isReady, items]);

  if (items.length === 0) {
    return null;
  }

  const handleItemClick = (index, itemOnClick) => {
    if (!isControlled) setInternalActiveIndex(index);
    onTabChange?.(index);
    itemOnClick?.();
  };

  return (
    <nav
      className={cn(
        'relative inline-flex items-center h-14 rounded-xl bg-card text-foreground border border-border px-2',
        showLabels && 'h-12 md:h-14',
        className
      )}
    >
      {items.map(({ id, icon, label, onClick }, index) => (
        <button
          key={id}
          ref={(el) => (navItemRefs.current[index] = el)}
          type="button"
          className={cn(
            'relative z-20 flex h-full cursor-pointer items-center justify-center gap-2 px-4 md:px-5 transition-colors rounded-lg',
            activeIndex === index ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground',
            iconContainerClassName
          )}
          onClick={() => handleItemClick(index, onClick)}
          aria-label={label}
          aria-selected={activeIndex === index}
        >
          {cloneElement(icon, {
            className: cn(
              'w-5 h-5 shrink-0 transition-opacity duration-200',
              activeIndex === index ? 'opacity-100' : 'opacity-50',
              icon.props?.className,
              iconClassName
            ),
          })}
          {showLabels && label && (
            <span className="hidden sm:inline text-sm whitespace-nowrap">{label}</span>
          )}
        </button>
      ))}

      <div
        ref={limelightRef}
        className={cn(
          'absolute bottom-0 z-10 h-[3px] rounded-full bg-primary transition-[left] duration-300 ease-out',
          !isReady && 'transition-none',
          limelightClassName
        )}
        style={{ left: '-999px' }}
      >
        <div
          className="absolute left-[-30%] top-0 w-[160%] h-8 [clip-path:polygon(5%_100%,25%_0,75%_0,95%_100%)] bg-gradient-to-b from-primary/20 to-transparent pointer-events-none"
          aria-hidden
        />
      </div>
    </nav>
  );
}
